// Secure-context-safe UUID v4.
//
// `crypto.randomUUID()` is ONLY exposed in secure contexts (HTTPS or
// localhost). Over plain HTTP on a LAN IP — e.g. testing from a phone at
// http://192.168.1.101:5173 — it is `undefined`, so calling it throws
// "crypto.randomUUID is not a function" and breaks every request that mints a
// correlation ID (including login).
//
// `crypto.getRandomValues()` IS available in non-secure contexts, so we fall
// back to a getRandomValues-based v4, then to Math.random as a last resort.
export function safeRandomUUID(): string {
  const c = globalThis.crypto as Crypto | undefined

  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID()
  }

  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'))
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  }

  // Last resort (non-crypto): fine for correlation IDs / local queue keys.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    const v = ch === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
